import { useTracking } from '@act/react-components/tracking';
import { NeedDto, NeedsActionDto } from '@act/shared/data-transfer-objects';
import { Button, IconLibrary, SmartContentDisplay } from '@act/storybook';
import {
  Box,
  Divider,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ClockCounterClockwiseIcon, PlusIcon } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import HtmlDisplayer from 'react-components/components/html-displayer';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hooks';
import { getMemberNeeds, needsSelectors } from 'redux/slices/needs.slice';
import { useAppDispatch } from 'redux/store-hooks';
import NeedStatusTag from '../components/need-status-tag';
import syvaNeedsUtils from '../syva-needs-utils';
import { AddNeedContent } from './AddNeedContent';
import NeedTimeAlert from './components/need-time-alert';
import { GoalsSidePanel } from './GoalsSidePanel';
import NeedDetailHistory from './NeedDetailHistory';
import { NeedsDetailAddAction } from './NeedsDetailAddAction';

export interface NeedDetailContentProps {
  needId: number;
  onClose: () => void;
  open: boolean;
  onAnimationComplete?: () => void;
}

export const NeedDetailContent = ({
  needId,
  onClose,
  open,
  onAnimationComplete,
}: NeedDetailContentProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { generateTrackingProps } = useTracking();
  const [isAddActionOpen, setIsAddActionOpen] = useState(false);
  const [editAction, setEditAction] = useState<NeedsActionDto | null>(null);
  const [editNeed, setEditNeed] = useState<NeedDto | null>(null);
  const [shouldRender, setShouldRender] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const needMetadata = metaSliceSelectors.useSelectNeedsMetadata();
  const need = needsSelectors.useSelectNeedById(needId);
  const member = membersAppSelectors.useSelectMemberById(need?.memberId);
  const issue = needMetadata.data.issues.find(i => i.id === need?.issueId);
  const category = needMetadata.data.categories.find(
    c => c.id === issue?.categoryId
  );
  const daysRemaining = syvaNeedsUtils.calculateRemainingDays(
    need?.expiresAt || 0
  );
  useEffect(() => {
    if (needId && !need) {
      dispatch(getMemberNeeds({ params: { memberId: member?.id } }));
    }
  }, [needId]);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
    }
    // Don't reset shouldRender when open becomes false - let animation complete
  }, [open]);

  const handleAnimationComplete = () => {
    setShouldRender(false);
    onAnimationComplete?.();
  };

  if (!need) {
    return (
      <Box sx={{ padding: '24px', textAlign: 'center' }}>
        <Typography variant="bodyMedium" color="text.secondary">
          Need not found
        </Typography>
      </Box>
    );
  }

  if (!shouldRender) return null;

  return (
    <>
      {editNeed && (
        <AddNeedContent
          open={!!editNeed}
          onClose={() => setEditNeed(null)}
          need={need}
        />
      )}
      {!editNeed &&
        (isAddActionOpen || editAction ? (
          <NeedsDetailAddAction
            open={isAddActionOpen || !!editAction}
            onClose={() => {
              setIsAddActionOpen(false);
              setEditAction(null);
            }}
            need={need}
            action={editAction}
          />
        ) : (
          shouldRender && (
            <GoalsSidePanel
              open={open}
              animated={true}
              setShouldRenderSidePanel={handleAnimationComplete}
              titleContent={
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px',
                    }}
                  >
                    <Typography
                      variant="titleLarge"
                      sx={{
                        fontSize: isMobile ? '1.25rem' : undefined,
                        lineHeight: isMobile ? 1.3 : undefined,
                      }}
                    >
                      {issue?.label}
                    </Typography>
                    <NeedStatusTag need={need} />
                  </Box>
                  <IconButton onClick={onClose}>
                    <IconLibrary iconName="X" size={24} weight="regular" />
                  </IconButton>
                </Box>
              }
              content={
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    gap: '8px',
                  }}
                >
                  {need?.reason && (
                    <SmartContentDisplay maxHeight={150}>
                      <Typography variant="bodyMedium">
                        <HtmlDisplayer innerHtml={need?.reason} />
                      </Typography>
                    </SmartContentDisplay>
                  )}
                  <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '49px',
                        borderRadius: '8px',
                        padding: '16px',
                        backgroundColor: theme.palette.navyShades[100],
                        marginBottom: '16px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                        }}
                      >
                        <Typography
                          variant="subheadMedium"
                          fontWeight="semibold"
                        >
                          Category
                        </Typography>
                        <Typography variant="bodyMedium">
                          {category?.label}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                        }}
                      >
                        <Typography
                          variant="subheadMedium"
                          fontWeight="semibold"
                        >
                          Remaining time
                        </Typography>
                        <Typography variant="bodyMedium">
                          {`${daysRemaining} days remaining`}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ marginBottom: '16px' }}>
                      <NeedTimeAlert need={need} showDaysRemaining={false} />
                    </Box>
                    <Divider sx={{ marginBottom: '16px' }} />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '16px',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ClockCounterClockwiseIcon size={24} />
                        <Typography variant="titleMedium">History</Typography>
                      </Box>
                      <Box sx={{ display: 'flex' }}>
                        <Button
                          variant="text"
                          size="large"
                          startIcon={<PlusIcon />}
                          onClick={() => setIsAddActionOpen(true)}
                          {...generateTrackingProps(
                            'open-add-action-from-call-button'
                          )}
                        >
                          <Typography
                            variant="titleMedium"
                            fontWeight="semibold"
                          >
                            Add Action
                          </Typography>
                        </Button>
                      </Box>
                    </Box>
                    <NeedDetailHistory
                      need={need}
                      editMode={true}
                      detailMode={true}
                      setEdit={setEditAction}
                      setEditNeed={setEditNeed}
                    />
                  </Box>
                </Box>
              }
              footerContent={
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: isMobile ? 'center' : 'flex-end',
                    width: '100%',
                  }}
                >
                  <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                      minWidth: isMobile ? '120px' : 'auto',
                      minHeight: '44px',
                      width: isMobile ? '100%' : 'auto',
                    }}
                  >
                    Save & close
                  </Button>
                </Box>
              }
            />
          )
        ))}
    </>
  );
};
